import React, { useState, useEffect } from 'react';
import { Container, Modal, Tab, Tabs, Button, Table, Form, Alert, Spinner, Row, Col } from 'react-bootstrap';
import './SetupReservationsScreen.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5004';

function SetupReservationsScreen() {
  const [showModal, setShowModal] = useState(true);
  const [activeTab, setActiveTab] = useState('configuration');
  
  // Market state
  const [markets, setMarkets] = useState([]);
  const [marketForm, setMarketForm] = useState({ name: '', description: '', active: true });
  const [showMarketModal, setShowMarketModal] = useState(false);
  const [editingMarket, setEditingMarket] = useState(null);
  const [marketLoading, setMarketLoading] = useState(false);
  const [marketError, setMarketError] = useState('');
  
  // Source state
  const [sources, setSources] = useState([]);
  const [sourceForm, setSourceForm] = useState({ name: '', description: '', active: true });
  const [showSourceModal, setShowSourceModal] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [sourceLoading, setSourceLoading] = useState(false);
  const [sourceError, setSourceError] = useState('');

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setActiveTab('configuration');
  };

  const handleSaveAndClose = () => {
    // Save any changes and close modal
    setShowModal(false);
    setActiveTab('configuration');
  };

  const handleSave = () => {
    // Save any changes but keep modal open
    console.log('Saving changes...');
  };

  // Market API functions
  const fetchMarkets = async () => {
    try {
      const response = await fetch(`${API_URL}/api/markets`);
      if (response.ok) {
        const data = await response.json();
        setMarkets(data);
      } else {
        setMarketError('Failed to fetch markets');
      }
    } catch (error) {
      setMarketError('Error fetching markets');
    }
  };

  const createMarket = async (marketData) => {
    try {
      const response = await fetch(`${API_URL}/api/markets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(marketData)
      });
      
      if (response.ok) {
        await fetchMarkets();
        return true;
      } else {
        const errorData = await response.json();
        setMarketError(errorData.message || 'Failed to create market');
        return false;
      }
    } catch (error) {
      setMarketError('Error creating market');
      return false;
    }
  };

  const updateMarket = async (id, marketData) => {
    try {
      const response = await fetch(`${API_URL}/api/markets/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(marketData)
      });
      
      if (response.ok) {
        await fetchMarkets();
        return true;
      } else {
        const errorData = await response.json();
        setMarketError(errorData.message || 'Failed to update market');
        return false;
      }
    } catch (error) {
      setMarketError('Error updating market');
      return false;
    }
  };

  const deleteMarket = async (id) => {
    if (window.confirm('Are you sure you want to delete this market?')) {
      try {
        const response = await fetch(`${API_URL}/api/markets/${id}`, {
          method: 'DELETE'
        });
        
        if (response.ok) {
          await fetchMarkets();
        } else {
          setMarketError('Failed to delete market');
        }
      } catch (error) {
        setMarketError('Error deleting market');
      }
    }
  };

  // Source API functions
  const fetchSources = async () => {
    try {
      const response = await fetch(`${API_URL}/api/sources`);
      if (response.ok) {
        const data = await response.json();
        setSources(data);
      } else {
        setSourceError('Failed to fetch sources');
      }
    } catch (error) {
      setSourceError('Error fetching sources');
    }
  };

  const createSource = async (sourceData) => {
    try {
      const response = await fetch(`${API_URL}/api/sources`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sourceData)
      });
      
      if (response.ok) {
        await fetchSources();
        return true;
      } else {
        const errorData = await response.json();
        setSourceError(errorData.message || 'Failed to create source');
        return false;
      }
    } catch (error) {
      setSourceError('Error creating source');
      return false;
    }
  };

  const updateSource = async (id, sourceData) => {
    try {
      const response = await fetch(`${API_URL}/api/sources/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sourceData)
      });
      
      if (response.ok) {
        await fetchSources();
        return true;
      } else {
        const errorData = await response.json();
        setSourceError(errorData.message || 'Failed to update source');
        return false;
      }
    } catch (error) {
      setSourceError('Error updating source');
      return false;
    }
  };

  const deleteSource = async (id) => {
    if (window.confirm('Are you sure you want to delete this source?')) {
      try {
        const response = await fetch(`${API_URL}/api/sources/${id}`, {
          method: 'DELETE'
        });
        
        if (response.ok) {
          await fetchSources();
        } else {
          setSourceError('Failed to delete source');
        }
      } catch (error) {
        setSourceError('Error deleting source');
      }
    }
  };

  // Market modal handlers
  const handleAddMarket = () => {
    setEditingMarket(null);
    setMarketForm({ name: '', description: '', active: true });
    setMarketError('');
    setShowMarketModal(true);
  };

  const handleEditMarket = (market) => {
    setEditingMarket(market);
    setMarketForm({ name: market.name, description: market.description || '', active: market.active });
    setMarketError('');
    setShowMarketModal(true);
  };

  const handleCloseMarketModal = () => {
    setShowMarketModal(false);
    setEditingMarket(null);
    setMarketForm({ name: '', description: '', active: true });
    setMarketError('');
  };

  const handleMarketInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setMarketForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleMarketSubmit = async (e) => {
    e.preventDefault();
    setMarketLoading(true);
    
    if (editingMarket) {
      const success = await updateMarket(editingMarket.id, marketForm);
      if (success) {
        handleCloseMarketModal();
      }
    } else {
      const success = await createMarket(marketForm);
      if (success) {
        handleCloseMarketModal();
      }
    }
    
    setMarketLoading(false);
  };

  // Source modal handlers
  const handleAddSource = () => {
    setEditingSource(null);
    setSourceForm({ name: '', description: '', active: true });
    setSourceError('');
    setShowSourceModal(true);
  };

  const handleEditSource = (source) => {
    setEditingSource(source);
    setSourceForm({ name: source.name, description: source.description || '', active: source.active });
    setSourceError('');
    setShowSourceModal(true);
  };

  const handleCloseSourceModal = () => {
    setShowSourceModal(false);
    setEditingSource(null);
    setSourceForm({ name: '', description: '', active: true });
    setSourceError('');
  };

  const handleSourceInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSourceForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSourceSubmit = async (e) => {
    e.preventDefault();
    setSourceLoading(true);
    
    if (editingSource) {
      const success = await updateSource(editingSource.id, sourceForm);
      if (success) {
        handleCloseSourceModal();
      }
    } else {
      const success = await createSource(sourceForm);
      if (success) {
        handleCloseSourceModal();
      }
    }
    
    setSourceLoading(false);
  };

  // Fetch data when component mounts
  useEffect(() => {
    if (activeTab === 'market') {
      fetchMarkets();
    } else if (activeTab === 'source') {
      fetchSources();
    }
  }, [activeTab]);

  const renderMarketTab = () => (
    <div className="tab-content">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>Market Management</h4>
        <Button variant="primary" onClick={handleAddMarket}>
          Add Market
        </Button>
      </div>
      
      {marketError && <Alert variant="danger" onClose={() => setMarketError('')} dismissible>{marketError}</Alert>}
      
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>Name</th>
            <th>Description</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {markets.map(market => (
            <tr key={market.id}>
              <td><strong>{market.name}</strong></td>
              <td>{market.description || '-'}</td>
              <td>
                <span className={`badge ${market.active ? 'bg-success' : 'bg-secondary'}`}>
                  {market.active ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td>
                <Button variant="outline-primary" size="sm" onClick={() => handleEditMarket(market)} className="me-2">
                  Edit
                </Button>
                <Button variant="outline-danger" size="sm" onClick={() => deleteMarket(market.id)}>
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );

  const renderSourceTab = () => (
    <div className="tab-content">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>Source Management</h4>
        <Button variant="primary" onClick={handleAddSource}>
          Add Source
        </Button>
      </div>
      
      {sourceError && <Alert variant="danger" onClose={() => setSourceError('')} dismissible>{sourceError}</Alert>}
      
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>Name</th>
            <th>Description</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sources.map(source => (
            <tr key={source.id}>
              <td><strong>{source.name}</strong></td>
              <td>{source.description || '-'}</td>
              <td>
                <span className={`badge ${source.active ? 'bg-success' : 'bg-secondary'}`}>
                  {source.active ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td>
                <Button variant="outline-primary" size="sm" onClick={() => handleEditSource(source)} className="me-2">
                  Edit
                </Button>
                <Button variant="outline-danger" size="sm" onClick={() => deleteSource(source.id)}>
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );

  return (
    <Container fluid className="setup-reservations-screen">
      <div className="screen-header">
        <h1>Setup - Reservations</h1>
        <p>Configure reservations settings and management</p>
      </div>

      <div className="screen-content">
        {/* Main Setup Modal */}
        <Modal 
          show={showModal} 
          onHide={handleCloseModal}
          size="xl"
          backdrop="static"
          keyboard={false}
          className="setup-modal"
        >
          <Modal.Header>
            <Modal.Title>Setup - Reservations</Modal.Title>
            <Button
              variant="link"
              onClick={handleModalClose}
              className="close-button"
            >
              ×
            </Button>
          </Modal.Header>
          
          <Modal.Body>
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="setup-tabs"
            >
              <Tab eventKey="configuration" title="Configuration">
                <div className="tab-content">
                  <h4>Configuration</h4>
                  <p>Configuration options will be added here in the future.</p>
                </div>
              </Tab>
              <Tab eventKey="market" title="Market">
                {renderMarketTab()}
              </Tab>
              <Tab eventKey="source" title="Source">
                {renderSourceTab()}
              </Tab>
            </Tabs>
          </Modal.Body>
          
          <Modal.Footer>
            <Button onClick={handleSaveAndClose}>
              Save & Close
            </Button>
            <Button onClick={handleSave}>
              Save
            </Button>
            <Button onClick={handleModalClose}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Market Modal */}
        <Modal show={showMarketModal} onHide={handleCloseMarketModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{editingMarket ? 'Edit Market' : 'Add Market'}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleMarketSubmit}>
            <Modal.Body>
              {marketError && <Alert variant="danger">{marketError}</Alert>}
              
              <Form.Group className="mb-3">
                <Form.Label>Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={marketForm.name}
                  onChange={handleMarketInputChange}
                  required
                  placeholder="Enter market name"
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={marketForm.description}
                  onChange={handleMarketInputChange}
                  placeholder="Enter market description"
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  name="active"
                  checked={marketForm.active}
                  onChange={handleMarketInputChange}
                  label="Active"
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseMarketModal}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={marketLoading}>
                {marketLoading ? <Spinner animation="border" size="sm" /> : (editingMarket ? 'Update' : 'Create')}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Source Modal */}
        <Modal show={showSourceModal} onHide={handleCloseSourceModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{editingSource ? 'Edit Source' : 'Add Source'}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSourceSubmit}>
            <Modal.Body>
              {sourceError && <Alert variant="danger">{sourceError}</Alert>}
              
              <Form.Group className="mb-3">
                <Form.Label>Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={sourceForm.name}
                  onChange={handleSourceInputChange}
                  required
                  placeholder="Enter source name"
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={sourceForm.description}
                  onChange={handleSourceInputChange}
                  placeholder="Enter source description"
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  name="active"
                  checked={sourceForm.active}
                  onChange={handleSourceInputChange}
                  label="Active"
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseSourceModal}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={sourceLoading}>
                {sourceLoading ? <Spinner animation="border" size="sm" /> : (editingSource ? 'Update' : 'Create')}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </div>
    </Container>
  );
}

export default SetupReservationsScreen;
